import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { initialState } from './initialState';
import { PAYMENT_BILL_TYPE } from '../constants';
import type { EditableManualBill, EditableOutstandingBill, SelectedCluster } from '../types/payments.types';
import { splitOutstandingBills } from '../utils';

const paymentsSlice = createSlice({
  initialState,
  name: 'Payments',
  reducers: {
    addManualBills(state, action: PayloadAction<EditableManualBill>) {
      const { AmountDueFromPatient, BillType } = action.payload;
      state.manualBills.push({
        ...action.payload,
        isFullyPaidBill:
          BillType === PAYMENT_BILL_TYPE.MANUAL_USER ||
          (AmountDueFromPatient <= 0 && BillType === PAYMENT_BILL_TYPE.MANUAL),
      });
    },
    deleteManualBill(state, action: PayloadAction<{ invoiceNumber: string }>) {
      state.manualBills = state.manualBills.filter((record) => action.payload.invoiceNumber !== record.InvoiceNumber);
    },
    resetReferenceNumber: (state) => {
      state.paymentReceipt.referenceNumber = initialState.paymentReceipt.referenceNumber;
    },
    resetSelectedCluster: (state) => {
      state.selectedCluster = initialState.selectedCluster;
    },
    resetStatePayments: (state) => {
      state.outstandingBills = initialState.outstandingBills;
      state.manualBills = initialState.manualBills;
    },
    setCheckAllOutstandingBills(state, action: PayloadAction<boolean>) {
      state.outstandingBills.outstanding.forEach((record) => {
        record.isChecked = action.payload;
      });
    },
    setOutstandingBills(state, action: PayloadAction<EditableOutstandingBill[]>) {
      const { outstanding, pending } = splitOutstandingBills(action.payload);

      state.outstandingBills.outstanding = outstanding.map((record) => {
        const matchExistingBill = state.outstandingBills.outstanding.find(
          (b) => b.InvoiceNumber === record.InvoiceNumber
        );

        if (matchExistingBill) {
          return {
            ...matchExistingBill,
            ...record,
            isChecked: matchExistingBill.isChecked,
            updatedAmount: matchExistingBill.updatedAmount,
          };
        }

        return {
          ...record,
          isChecked: true,
          updatedAmount: record.AmountDueFromPatient > 0 ? record.AmountDueFromPatient : 0,
        };
      });

      state.outstandingBills.pending = pending.map((p) => ({
        ...p,
        isChecked: false,
      }));
    },
    setReferenceNumber: (state, action: PayloadAction<string>) => {
      state.paymentReceipt.referenceNumber = action.payload;
    },
    setSelectedCluster(state, action: PayloadAction<SelectedCluster>) {
      state.selectedCluster = action.payload;
    },
    updateManualBill(state, action: PayloadAction<{ data: Partial<EditableManualBill>; id: string }>) {
      const { data, id } = action.payload;

      state.manualBills.forEach((record) => {
        if (record.InvoiceNumber === id) {
          Object.assign(record, data);
        }
      });
    },
    updateOutstandingBill(state, action: PayloadAction<{ data: Partial<EditableOutstandingBill>; id: string }>) {
      const { data, id } = action.payload;

      state.outstandingBills.outstanding.forEach((record) => {
        if (record.InvoiceNumber === id) {
          Object.assign(record, data);
        }
      });
    },
    validateEditAmount: (state, action: PayloadAction<boolean>) => {
      state.isValidEditAmount = action.payload;
    },
  },
});

export const { actions: paymentsAction } = paymentsSlice;

export default paymentsSlice.reducer;
